/**
 * Sample a pixel from a PDF page and return its hex color.
 * Used exclusively for the colored eyedropper cursor during color point placement.
 */

import { Core } from '@pdftron/webviewer';

function rgbToHex(r: number, g: number, b: number): string {
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
}

export async function sampleHexColorFromPDF(
  docViewer: Core.DocumentViewer,
  pageNumber: number,
  x: number,
  y: number,
): Promise<string> {
  const doc = docViewer.getDocument();
  const zoom = 1.0;
  const pageInfo = doc.getPageInfo(pageNumber);

  const canvas = await new Promise<HTMLCanvasElement>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error('Canvas load timeout - PDF rendering took too long'));
    }, 3000);

    try {
      doc.loadCanvas({
        pageNumber,
        zoom,
        drawComplete: (canvasOrImage: HTMLCanvasElement | HTMLImageElement) => {
          clearTimeout(timeoutId);

          if ('getContext' in canvasOrImage && typeof canvasOrImage.getContext === 'function') {
            resolve(canvasOrImage as HTMLCanvasElement);
            return;
          }

          const canvas = document.createElement('canvas');
          canvas.width = canvasOrImage.width;
          canvas.height = canvasOrImage.height;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Could not create canvas context for image'));
            return;
          }

          ctx.drawImage(canvasOrImage, 0, 0);
          resolve(canvas);
        },
      });
    } catch (error) {
      clearTimeout(timeoutId);
      reject(error);
    }
  });

  const canvasX = Math.round((x / pageInfo.width) * canvas.width);
  const canvasY = Math.round((y / pageInfo.height) * canvas.height);

  if (canvasX < 0 || canvasX >= canvas.width || canvasY < 0 || canvasY >= canvas.height) {
    throw new Error(`Coordinates out of bounds: (${canvasX}, ${canvasY})`);
  }

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Could not get canvas context');
  }

  const pixelData = context.getImageData(canvasX, canvasY, 1, 1).data;
  return rgbToHex(pixelData[0], pixelData[1], pixelData[2]);
}
